import Naver, { EfProductNaverProps, EfProductNaverItemProps } from '@elem/ui/ef-product-naver';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import Overview from './tabs/Overview';
import { theme } from './config/page';
import Support from './tabs/Support';
import Spec from './tabs/Spec';
import Bundle from './tabs/Bundle';
import css from './index.module.scss';
import useProductMsg from './context/getProduct';
import { IPageStatus } from './config/IPageStatus';
import { useProduct } from './ProductProvider';
import FreeQuote from './comps/FreeQuote';
import { useLang } from '@/i18n';
import './reset.scss';
import gtm from '@/utils/gtm';

const DPUX: React.FC = () => {
    const { state, dispatch } = useProduct();
    const { t } = useLang('dpux');
    const { locale = '' } = useRouter();
    const { productMsg, productSupport, productSpecs, activeTab, pageStatus } = state;
    const [activetedTabs, setActivetedTabs] = useState<string[]>([activeTab]);

    const naverProps: EfProductNaverProps = {
        className: pageStatus,
        theme: 'black',
        title: productMsg?.name || t('naver_title'),
        titleTabs:
            locale === 'us'
                ? [
                      {
                          label: productMsg?.name || t('naver_title'),
                          href: 'delta-pro-ultra-x',
                          h5Index: true,
                      },
                      {
                          label: t('naver_title_shp3'),
                          href: 'smart-home-panel-3',
                      },
                  ]
                : undefined,
        tabs: [
            {
                key: 'Product',
                text: t('naver_tab_product'),
            },
            {
                key: 'Bundle',
                text: t('naver_tab_bundle'),
            },
            {
                key: 'Spec',
                text: t('naver_tab_spec'),
            },
            {
                key: 'Support',
                text: t('naver_tab_support'),
            },
            ...(locale === 'us'
                ? [
                      {
                          key: 'Comparison',
                          text: t('naver_tab_comparison'),
                          href: '#Solutions', // 元素 id
                      },
                  ]
                : []),
        ],
        productHighlightIt: true, // 开启滚动到位置自动选中
        hideClickFnTabs: ['Comparison'],
        buyItem:
            pageStatus === IPageStatus.TEASER
                ? {
                      key: 'Product',
                      text: t('naver_tab_early'),
                  }
                : {
                      key: 'Buy',
                      text: t('naver_tab_buy'),
                      url: productMsg?.buyLink || `https://${locale}.ecoflow.com/products/delta-pro-ultra-x`,
                  },
        activeTab,
        buyNowGtm:
            pageStatus === IPageStatus.TEASER
                ? {
                      event: 'uaEvent',
                      event_name: 'product_navigation',
                      navigation_name: t('naver_tab_early'),
                      product_name: productMsg?.name || t('naver_title'),
                  }
                : {
                      event: 'uaEvent',
                      event_name: 'buy',
                      product_name: 'EcoFlow DELTA Pro Ultra X',
                      position: 'Product Preview',
                  },
        callbackBuy: () => {
            if (pageStatus === IPageStatus.TEASER) {
                if (activeTab !== 'Product') {
                    dispatch({
                        type: 'changeProductTab',
                        payload: 'Product',
                    });
                }
                const TargetD: HTMLElement | null = document.getElementById('delta-pro-ultra-x-D');
                if (TargetD) {
                    setTimeout(() => {
                        TargetD.scrollIntoView({
                            behavior: 'smooth',
                        });
                    }, 0);
                }
            }
        },
        callbackTab: (tab: EfProductNaverItemProps) => {
            if (tab.key !== activeTab) {
                if (tab?.key === 'Comparison') {
                    dispatch({
                        type: 'changeProductTab',
                        payload: 'Product',
                    });
                    setTimeout(() => {
                        const target = document.getElementById('Solutions');
                        target?.scrollIntoView();
                    }, 10);
                    return;
                }

                if (!activetedTabs.includes(tab.key)) {
                    setActivetedTabs([...activetedTabs, tab.key]);
                }
                dispatch({
                    type: 'changeProductTab',
                    payload: tab.key,
                });
                window.sessionStorage.removeItem('toTop-scrollTop');
                window.scrollTo({
                    top: 0,
                    behavior: 'instant',
                });
            }
        },
    };

    useProductMsg();

    /** 检测浏览器是否支持使用 webp */
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const webP = new Image();
        webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
        webP.onload = () => {
            dispatch({
                type: 'changeCanWebp',
                payload: 1,
            });
        };
        webP.onerror = () => {
            dispatch({
                type: 'changeCanWebp',
                payload: -1,
            });
        };
    }, [dispatch]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const loadPageBaseGtm = () => {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'product_page_load',
                });
            };
            loadPageBaseGtm();
        }
    }, []);

    return (
        <>
            <div className={`delta-pro-ultra-x ${css.section}`} style={{ minHeight: '100vh' }}>
                <Naver {...naverProps} />
                {typeof window !== 'undefined' && createPortal(<FreeQuote />, document.querySelector('#Widgets-box-in2-right-id') || document.body)}

                {activetedTabs.includes('Product') && (
                    <div className="page-tab tab-overview" style={{ display: activeTab === 'Product' ? 'block' : 'none' }}>
                        <Overview setActivetedTabs={setActivetedTabs} />
                    </div>
                )}
                {activetedTabs.includes('Bundle') && (
                    <div className="page-tab tab-Spec" style={{ display: activeTab === 'Bundle' ? 'block' : 'none' }}>
                        <Bundle />
                    </div>
                )}
                {activetedTabs.includes('Spec') && (
                    <div className="page-tab tab-Spec" style={{ display: activeTab === 'Spec' ? 'block' : 'none' }}>
                        <Spec productSpecs={productSpecs} theme={theme} />
                    </div>
                )}
                {activetedTabs.includes('Support') && (
                    <div className="page-tab tab-support" style={{ display: activeTab === 'Support' ? 'block' : 'none' }}>
                        <Support supportData={productSupport} productMsg={productMsg} theme={theme} />
                    </div>
                )}
            </div>
        </>
    );
};

export default DPUX;
